/**
 * API Client Service
 *
 * Provides a centralized HTTP client for communicating with the Partner Portal backend.
 * Handles authentication, error handling, and type-safe API calls.
 *
 * Usage:
 *   const api = new ApiClient(idToken);
 *   const partners = await api.getPartners();
 */

import { env } from '@/config/env';

/**
 * Base configuration for the API client
 */
const API_BASE_URL = env.apiUrl;

/**
 * Backend error response structure
 * Matches the ErrorResponse interface from backend
 */
export interface BackendErrorResponse {
  error: string;
  message: string;
  statusCode: number;
  timestamp: string;
  path?: string;
  details?: unknown;
}

/**
 * Type guard to check if an object is a BackendErrorResponse
 */
function isBackendErrorResponse(obj: unknown): obj is BackendErrorResponse {
  return (
    typeof obj === 'object' &&
    obj !== null &&
    'error' in obj &&
    'message' in obj &&
    'statusCode' in obj &&
    typeof (obj as BackendErrorResponse).error === 'string' &&
    typeof (obj as BackendErrorResponse).message === 'string' &&
    typeof (obj as BackendErrorResponse).statusCode === 'number'
  );
}

/**
 * Custom error class for API errors
 * Provides structured error information from the backend
 */
export class ApiError extends Error {
  public errorCode?: string;
  public path?: string;
  public details?: unknown;

  constructor(
    message: string,
    public status: number,
    public response?: unknown
  ) {
    super(message);
    this.name = 'ApiError';

    // Extract additional error information if available
    if (isBackendErrorResponse(response)) {
      this.errorCode = response.error;
      this.path = response.path;
      this.details = response.details;
    }
  }
}

/**
 * Partner data structure
 */
export interface Partner {
  id: string;
  name: string;
  contactEmail: string;
  contactPhone?: string;
  status: 'active' | 'inactive' | 'pending';
  schoolIds: string[];
  createdAt: string;
  updatedAt: string;
}

/**
 * Student data structure
 */
export interface Student {
  id: string;
  firstName: string;
  lastName: string;
  email?: string;
  schoolId: string;
  grade?: number;
  academicYear: string;
}

/**
 * Transcript record from the transcript API
 */
export interface Transcript {
  id: number;
  user_id: number;
  student_first_name: string;
  student_last_name: string;
  student_email: string;
  course_id: number;
  course_title: string;
  course_code: string;
  course_type: string;
  /** Numeric string; null for NON-CREDIT courses. */
  credits: string | null;
  state: string;
  certification_date: string;
  score_submitted: string;
  course_start_date: string;
  program_id: string;
  program_name: string;
}

/**
 * Course option for filter dropdown
 */
export interface TranscriptCourse {
  id: number;
  /** Every spcc_courses id behind this (title, code) option — the catalog
   *  has duplicate rows, so filters must include all of them. */
  ids?: number[];
  title: string;
  course_code: string;
}

/**
 * Paginated transcript response
 */
export interface TranscriptListResponse {
  items: Transcript[];
  total: number;
  page: number;
  per_page: number;
}

/**
 * Student-grouped transcript row (one per student)
 */
export interface StudentTranscriptRow {
  user_id: number;
  student_first_name: string;
  student_last_name: string;
  student_email: string;
  course_count: number;
  total_credits: number;
  latest_certification_date: string | null;
  latest_transcript_id: number;
  latest_course_title: string;
  earliest_certification_date: string | null;
  transcript_ids: number[];
  // Present when the review_status filter is active: the server-computed status
  review_status?: 'reviewed' | 'updated' | 'needs_review';
}

/**
 * Paginated student-grouped transcript response
 */
export interface StudentTranscriptListResponse {
  items: StudentTranscriptRow[];
  total: number;
  page: number;
  per_page: number;
}

/**
 * Download status response for transcript download tracking
 */
export interface TranscriptDownloadStatusResponse {
  my_downloads: Record<number, { downloaded_at: string; download_origin?: string }>;
  org_downloads: Record<
    number,
    { user_email: string; downloaded_at: string; download_origin?: string }
  >;
}

/**
 * Portal configuration for a partner (backend Firestore).
 * portal_launch_date: transcripts certified before this date were sent to the
 * partner by email pre-Portal and read as reviewed (SOPHIA-35018).
 */
export interface PartnerPortalConfig {
  portal_launch_date: string | null;
}

/**
 * Query parameters for transcript list
 */
export interface TranscriptQueryParams {
  search?: string;
  student_id?: string;
  course_ids?: string;
  date_from?: string;
  date_to?: string;
  page?: number;
  per_page?: number;
  sort?: string;
  order?: 'asc' | 'desc';
  // by-student review filter (server-side): a comma-separated subset of
  // reviewed,updated,needs_review, plus the partner's launch date for the
  // pre-launch rule. Omitted/all-three => no filtering
  review_status?: string;
  portal_launch_date?: string;
}

/**
 * A single enrollment-level row from the student progress API
 */
export interface StudentProgressItem {
  user_id: number;
  first_name: string;
  last_name: string;
  email: string;
  course_count: number;
  completed_count: number;
  in_progress_count: number;
  certified_count: number;
  last_activity: string | null;
  /** YYYY-MM-DD; null when the learner has none on file (SOPHIA-35554) */
  birthdate: string | null;
}

/**
 * A single course from the student detail endpoint
 */
export interface StudentCourse {
  course_id: number;
  course_title: string;
  course_code: string;
  credits: string;
  state: string;
  percent_completed: number | null;
  certification_date: string | null;
  course_start_date: string | null;
  last_activity: string | null;
  final_score: number | null;
}

/**
 * Student detail response from GET /students/:id
 */
export interface StudentDetailResponse {
  student: {
    id: number;
    first_name: string;
    last_name: string;
    email: string;
    program_id: string;
  };
  courses: StudentCourse[];
  summary: {
    total_courses: number;
    completed: number;
    in_progress: number;
    last_activity: string | null;
    days_inactive: number | null;
    risk_level: 'amber' | 'red' | null;
  };
}

/**
 * Query parameters for student progress list
 */
export interface StudentProgressQueryParams {
  search?: string;
  /** Maps to STATE_FILTERS keys in the student API: in_progress | completed | not_started */
  status?: string;
  course_ids?: string;
  date_from?: string;
  date_to?: string;
  page?: number;
  per_page?: number;
  sort?: string;
  order?: 'asc' | 'desc';
}

/**
 * Student progress list response from the student API
 */
export interface StudentProgressResponse {
  items: StudentProgressItem[];
  total: number;
  page: number;
  per_page: number;
  summary: {
    distinct_students: number;
    active_students: number;
    active_courses: number;
  };
}

/**
 * API response wrapper for paginated results
 */
export interface PaginatedResponse<T> {
  data: T[];
  total: number;
  page: number;
  pageSize: number;
}

/**
 * API Client
 *
 * Provides methods for interacting with the Partner Portal backend API.
 * All methods require authentication via Firebase ID token.
 */
export class ApiClient {
  private idToken: string | null;
  private baseUrl: string;

  /**
   * Create a new API client instance
   *
   * @param idToken Firebase ID token for authentication
   * @param baseUrl Optional base URL override (defaults to VITE_API_URL env var)
   */
  constructor(idToken: string | null = null, baseUrl: string = API_BASE_URL) {
    this.idToken = idToken;
    this.baseUrl = baseUrl;
  }

  /**
   * Update the authentication token
   *
   * @param idToken New Firebase ID token
   */
  setToken(idToken: string | null): void {
    this.idToken = idToken;
  }

  /**
   * Make an authenticated HTTP request
   *
   * @param endpoint API endpoint path (e.g., '/api/partners')
   * @param options Fetch options (method, body, headers, etc.)
   * @returns Promise resolving to parsed JSON response
   * @throws ApiError if request fails
   */
  private async request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    // Add existing headers from options
    if (options.headers) {
      const existingHeaders = new Headers(options.headers);
      existingHeaders.forEach((value, key) => {
        headers[key] = value;
      });
    }

    // Add authorization header if token is available
    if (this.idToken) {
      headers['Authorization'] = `Bearer ${this.idToken}`;
    }

    try {
      const response = await fetch(url, {
        ...options,
        headers,
      });

      // Handle non-OK responses
      if (!response.ok) {
        let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
        let errorData: unknown;

        try {
          errorData = await response.json();
          // Extract error message from structured backend response
          if (isBackendErrorResponse(errorData)) {
            errorMessage = errorData.message;
          } else if (errorData && typeof errorData === 'object' && 'message' in errorData) {
            errorMessage = (errorData as { message: string }).message;
          }
        } catch {
          // If JSON parsing fails, use default error message
        }

        throw new ApiError(errorMessage, response.status, errorData);
      }

      // Handle 204 No Content
      if (response.status === 204) {
        return {} as T;
      }

      // Parse and return JSON response
      const data = await response.json();
      return data as T;
    } catch (error) {
      // Re-throw ApiErrors
      if (error instanceof ApiError) {
        throw error;
      }

      // Handle network errors or other fetch failures
      if (error instanceof Error) {
        throw new ApiError(`Network error: ${error.message}`, 0);
      }

      throw new ApiError('An unknown error occurred', 0);
    }
  }

  /**
   * Health check endpoint
   *
   * @returns Health status of the backend
   */
  async healthCheck(): Promise<{ status: string; timestamp: string; service: string }> {
    return this.request('/health');
  }

  /**
   * Get current authenticated user info
   *
   * @returns User information from the backend
   */
  async getMe(): Promise<{ user: unknown; message: string }> {
    return this.request('/api/me');
  }

  // Partner endpoints

  /**
   * Get all partners accessible to the authenticated user
   *
   * @returns Array of partners
   */
  async getPartners(): Promise<Partner[]> {
    const response = await this.request<{ partners: Partner[]; count: number }>('/api/partners');
    return response.partners;
  }

  /**
   * Get a specific partner by ID
   *
   * @param partnerId Partner ID
   * @returns Partner data
   */
  async getPartner(partnerId: string): Promise<Partner> {
    return this.request(`/api/partners/${partnerId}`);
  }

  /**
   * Create a new partner
   *
   * @param data Partner data
   * @returns Created partner
   */
  async createPartner(data: Omit<Partner, 'id' | 'createdAt' | 'updatedAt'>): Promise<Partner> {
    return this.request('/api/partners', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  /**
   * Update an existing partner
   *
   * @param partnerId Partner ID
   * @param data Partial partner data to update
   * @returns Updated partner
   */
  async updatePartner(
    partnerId: string,
    data: Partial<Omit<Partner, 'id' | 'createdAt' | 'updatedAt'>>
  ): Promise<Partner> {
    return this.request(`/api/partners/${partnerId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  /**
   * Delete a partner
   *
   * @param partnerId Partner ID
   */
  async deletePartner(partnerId: string): Promise<void> {
    return this.request(`/api/partners/${partnerId}`, {
      method: 'DELETE',
    });
  }

  // Student endpoints

  /**
   * Get students by school ID
   *
   * @param schoolId School ID
   * @param params Optional query parameters (page, pageSize, academicYear)
   * @returns Paginated student data
   */
  async getStudentsBySchool(
    schoolId: string,
    params?: { page?: number; pageSize?: number; academicYear?: string }
  ): Promise<PaginatedResponse<Student>> {
    const queryParams = new URLSearchParams();
    if (params?.page !== undefined) queryParams.set('page', String(params.page));
    if (params?.pageSize !== undefined) queryParams.set('pageSize', String(params.pageSize));
    if (params?.academicYear) queryParams.set('academicYear', params.academicYear);

    const query = queryParams.toString();
    const endpoint = `/api/students/school/${schoolId}${query ? `?${query}` : ''}`;

    return this.request(endpoint);
  }

  /**
   * Get a specific student by ID
   *
   * @param studentId Student ID
   * @returns Student data
   */
  async getStudent(studentId: string): Promise<Student> {
    return this.request(`/api/students/${studentId}`);
  }

  /**
   * Search students across all accessible schools
   *
   * @param query Search query (name, email, student ID)
   * @param params Optional query parameters (page, pageSize)
   * @returns Paginated student search results
   */
  async searchStudents(
    query: string,
    params?: { page?: number; pageSize?: number }
  ): Promise<PaginatedResponse<Student>> {
    const queryParams = new URLSearchParams({ q: query });
    if (params?.page !== undefined) queryParams.set('page', String(params.page));
    if (params?.pageSize !== undefined) queryParams.set('pageSize', String(params.pageSize));

    return this.request(`/api/students/search?${queryParams.toString()}`);
  }

  // Transcript endpoints (backed by Ali's transcript API)

  /**
   * Get paginated transcripts for the current partner
   */
  async getTranscripts(
    partnerId: number,
    params?: TranscriptQueryParams
  ): Promise<TranscriptListResponse> {
    const queryParams = new URLSearchParams();
    if (params?.search) queryParams.set('search', params.search);
    if (params?.student_id) queryParams.set('student_id', params.student_id);
    if (params?.course_ids) queryParams.set('course_ids', params.course_ids);
    if (params?.date_from) queryParams.set('date_from', params.date_from);
    if (params?.date_to) queryParams.set('date_to', params.date_to);
    if (params?.page !== undefined) queryParams.set('page', String(params.page));
    if (params?.per_page !== undefined) queryParams.set('per_page', String(params.per_page));
    if (params?.sort) queryParams.set('sort', params.sort);
    if (params?.order) queryParams.set('order', params.order);

    const query = queryParams.toString();
    return this.request(`/transcripts${query ? `?${query}` : ''}`, {
      headers: new Headers({ 'X-Partner-Id': String(partnerId) }),
    });
  }

  /**
   * Get available courses for transcript filtering
   */
  async getTranscriptCourses(partnerId: number): Promise<{ items: TranscriptCourse[] }> {
    return this.request('/transcripts/courses', {
      headers: new Headers({ 'X-Partner-Id': String(partnerId) }),
    });
  }

  /**
   * Get dashboard summary stats (total transcripts + unique students) in one call
   */
  async getTranscriptStats(
    partnerId: number
  ): Promise<{ total_transcripts: number; unique_students: number }> {
    return this.request('/transcripts/stats', {
      headers: new Headers({ 'X-Partner-Id': String(partnerId) }),
    });
  }

  /**
   * Get transcript counts grouped by course (single query, replaces N+1)
   */
  async getTranscriptCourseCounts(
    partnerId: number,
    articulatedOnly: boolean = false,
    months?: number
  ): Promise<{ items: { id: number; title: string; course_code: string; count: number }[] }> {
    const params = new URLSearchParams();
    if (articulatedOnly) params.set('articulated_only', 'true');
    if (months) params.set('months', String(months));
    const qs = params.toString();
    return this.request(`/transcripts/courses/counts${qs ? `?${qs}` : ''}`, {
      headers: new Headers({ 'X-Partner-Id': String(partnerId) }),
    });
  }

  /**
   * Get monthly transcript trend data (single query, replaces N+1)
   */
  async getTranscriptTrend(
    partnerId: number,
    months: number = 12
  ): Promise<{ items: { month: string; label: string; count: number }[] }> {
    return this.request(`/transcripts/trend?months=${months}`, {
      headers: new Headers({ 'X-Partner-Id': String(partnerId) }),
    });
  }

  /**
   * Get top courses across all of Sophia with articulation flag for this partner
   */
  async getSophiaCourseCounts(
    partnerId: number,
    months?: number
  ): Promise<{
    items: {
      id: number;
      title: string;
      course_code: string;
      count: number;
      articulated: boolean;
    }[];
  }> {
    const params = new URLSearchParams({ limit: '999' });
    if (months) params.set('months', String(months));
    return this.request(`/transcripts/courses/counts/sophia?${params}`, {
      headers: new Headers({ 'X-Partner-Id': String(partnerId) }),
    });
  }

  /**
   * Get transcripts grouped by student (one row per student)
   */
  async getTranscriptsByStudent(
    partnerId: number,
    params?: TranscriptQueryParams
  ): Promise<StudentTranscriptListResponse> {
    const queryParams = new URLSearchParams();
    if (params?.search) queryParams.set('search', params.search);
    if (params?.course_ids) queryParams.set('course_ids', params.course_ids);
    if (params?.date_from) queryParams.set('date_from', params.date_from);
    if (params?.date_to) queryParams.set('date_to', params.date_to);
    if (params?.page !== undefined) queryParams.set('page', String(params.page));
    if (params?.per_page !== undefined) queryParams.set('per_page', String(params.per_page));
    if (params?.sort) queryParams.set('sort', params.sort);
    if (params?.order) queryParams.set('order', params.order);
    if (params?.review_status) queryParams.set('review_status', params.review_status);
    if (params?.portal_launch_date)
      queryParams.set('portal_launch_date', params.portal_launch_date);
    const query = queryParams.toString();
    return this.request(`/transcripts/by-student${query ? `?${query}` : ''}`, {
      headers: new Headers({ 'X-Partner-Id': String(partnerId) }),
    });
  }

  /**
   * Get a single transcript by ID
   */
  async getTranscript(partnerId: number, transcriptId: number): Promise<Transcript> {
    return this.request(`/transcripts/${transcriptId}`, {
      headers: new Headers({ 'X-Partner-Id': String(partnerId) }),
    });
  }

  // Student progress endpoints (backed by Ali's student API)

  /**
   * Get paginated student progress list with summary counts
   */
  async getStudentProgress(
    partnerId: number,
    params?: StudentProgressQueryParams
  ): Promise<StudentProgressResponse> {
    const queryParams = new URLSearchParams();
    if (params?.search) queryParams.set('search', params.search);
    // The student API reads `status` (STATE_FILTERS key); it ignores `state`
    if (params?.status) queryParams.set('status', params.status);
    if (params?.course_ids) queryParams.set('course_ids', params.course_ids);
    if (params?.date_from) queryParams.set('date_from', params.date_from);
    if (params?.date_to) queryParams.set('date_to', params.date_to);
    if (params?.per_page !== undefined) queryParams.set('per_page', String(params.per_page));
    if (params?.page !== undefined) queryParams.set('page', String(params.page));
    if (params?.sort) queryParams.set('sort', params.sort);
    if (params?.order) queryParams.set('order', params.order);
    const query = queryParams.toString();
    return this.request(`/students${query ? `?${query}` : ''}`, {
      headers: new Headers({ 'X-Partner-Id': String(partnerId) }),
    });
  }

  /**
   * Get student detail by user ID
   */
  async getStudentDetail(partnerId: number, userId: number): Promise<StudentDetailResponse> {
    return this.request(`/students/${userId}`, {
      headers: new Headers({ 'X-Partner-Id': String(partnerId) }),
    });
  }

  /**
   * Download student progress CSV export
   */
  async downloadStudentExport(
    partnerId: number,
    params?: StudentProgressQueryParams
  ): Promise<Blob> {
    const queryParams = new URLSearchParams();
    if (params?.search) queryParams.set('search', params.search);
    if (params?.status) queryParams.set('status', params.status);
    if (params?.course_ids) queryParams.set('course_ids', params.course_ids);
    if (params?.date_from) queryParams.set('date_from', params.date_from);
    if (params?.date_to) queryParams.set('date_to', params.date_to);
    const query = queryParams.toString();
    const url = `${this.baseUrl}/students/export${query ? `?${query}` : ''}`;
    const headers: Record<string, string> = { 'X-Partner-Id': String(partnerId) };
    if (this.idToken) {
      headers['Authorization'] = `Bearer ${this.idToken}`;
    }
    const response = await fetch(url, { headers });
    if (!response.ok) {
      throw new ApiError(
        `Export failed (${response.status} ${response.statusText})`,
        response.status
      );
    }
    return response.blob();
  }

  /**
   * Download transcripts CSV export in a single request (SOPHIA-35098).
   * Accepts the GET /transcripts filters; pagination params are ignored.
   */
  async downloadTranscriptExport(partnerId: number, params?: TranscriptQueryParams): Promise<Blob> {
    const queryParams = new URLSearchParams();
    if (params?.search) queryParams.set('search', params.search);
    if (params?.student_id) queryParams.set('student_id', params.student_id);
    if (params?.course_ids) queryParams.set('course_ids', params.course_ids);
    if (params?.date_from) queryParams.set('date_from', params.date_from);
    if (params?.date_to) queryParams.set('date_to', params.date_to);
    const query = queryParams.toString();
    const url = `${this.baseUrl}/transcripts/export${query ? `?${query}` : ''}`;
    const headers: Record<string, string> = { 'X-Partner-Id': String(partnerId) };
    if (this.idToken) {
      headers['Authorization'] = `Bearer ${this.idToken}`;
    }
    const response = await fetch(url, { headers });
    if (!response.ok) {
      throw new ApiError(
        `Export failed (${response.status} ${response.statusText})`,
        response.status
      );
    }
    return response.blob();
  }

  /**
   * Download a transcript PDF
   */
  async downloadTranscriptPdf(partnerId: number, transcriptId: number): Promise<Blob> {
    const url = `${this.baseUrl}/transcripts/${transcriptId}/download`;
    const headers: Record<string, string> = { 'X-Partner-Id': String(partnerId) };
    if (this.idToken) {
      headers['Authorization'] = `Bearer ${this.idToken}`;
    }
    const response = await fetch(url, { headers });
    if (!response.ok) {
      throw new ApiError(`Failed to download PDF: ${response.statusText}`, response.status);
    }
    return response.blob();
  }

  // Admin user management endpoints

  /**
   * List all Firebase Auth users, unpaginated (admin only) — the backend
   * returns the complete list and callers search/filter/sort locally
   * (SOPHIA-35249)
   */
  async getAdminUsers(): Promise<AdminUserListResponse> {
    return this.request('/api/admin/users');
  }

  /**
   * Get a single Firebase Auth user by UID (admin only)
   */
  async getAdminUser(uid: string): Promise<AdminUser> {
    return this.request(`/api/admin/users/${uid}`);
  }

  /**
   * Update a user's custom claims (admin only)
   */
  async updateUserClaims(
    uid: string,
    claims: { partnerId: string; roles: string[] }
  ): Promise<UpdateClaimsResponse> {
    return this.request(`/api/admin/users/${uid}/claims`, {
      method: 'PUT',
      body: JSON.stringify(claims),
    });
  }

  /**
   * Create a new user (admin only) — the user receives an invitation email
   * with an account setup link (SOPHIA-34868)
   */
  async createAdminUser(data: {
    email: string;
    firstName: string;
    lastName: string;
    partnerId: string;
    roles: string[];
  }): Promise<AdminUser & { message: string; emailSent: boolean }> {
    return this.request('/api/admin/users', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  /**
   * Enable or disable a user (admin only)
   */
  async setAdminUserDisabled(
    uid: string,
    disabled: boolean
  ): Promise<{ uid: string; disabled: boolean; message: string }> {
    return this.request(`/api/admin/users/${uid}/disable`, {
      method: 'PUT',
      body: JSON.stringify({ disabled }),
    });
  }

  /**
   * Remove a user — disables account and revokes access (admin only)
   */
  async removeAdminUser(uid: string): Promise<{ uid: string; message: string }> {
    return this.request(`/api/admin/users/${uid}`, {
      method: 'DELETE',
    });
  }

  // Client partner-user management endpoints

  /**
   * List users belonging to the caller's partner organization (or, for an
   * internal switcher user, the partnerId passed here — SOPHIA-35121)
   */
  async getPartnerUsersList(
    params?: {
      search?: string;
      page?: number;
      limit?: number;
    },
    partnerId?: number | null
  ): Promise<PartnerUserListResponse> {
    const qp = new URLSearchParams();
    if (params?.search) qp.set('search', params.search);
    if (params?.page !== undefined) qp.set('page', String(params.page));
    if (params?.limit !== undefined) qp.set('limit', String(params.limit));
    const q = qp.toString();
    return this.request(`/api/partner-users${q ? `?${q}` : ''}`, {
      headers: partnerId != null ? { 'X-Partner-Id': String(partnerId) } : undefined,
    });
  }

  /**
   * Create a new user in the caller's partner organization — the user
   * receives an invitation email with an account setup link (SOPHIA-34868)
   */
  async createPartnerUser(data: {
    firstName: string;
    lastName: string;
    email: string;
    roles?: string[];
    active?: boolean;
  }): Promise<PartnerUserMutationResponse & { emailSent: boolean }> {
    return this.request('/api/partner-users', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // Account-claiming endpoints (SOPHIA-34868) — public, token-authenticated;
  // safe to call with `new ApiClient(null)` before the user is signed in.

  /**
   * Resolve an account-claim token to its state (and invitee profile when valid)
   */
  async resolveAccountClaim(token: string): Promise<AccountClaimResolveResponse> {
    return this.request(`/api/account-claims/${encodeURIComponent(token)}`);
  }

  /**
   * Complete the account claim: sets the password and returns a Firebase
   * custom token for automatic sign-in
   */
  async claimAccount(token: string, password: string): Promise<AccountClaimResponse> {
    return this.request(`/api/account-claims/${encodeURIComponent(token)}`, {
      method: 'POST',
      body: JSON.stringify({ password }),
    });
  }

  /**
   * Request a fresh invitation email from an expired claim link
   */
  async resendAccountClaim(token: string): Promise<{ state: string; message: string }> {
    return this.request(`/api/account-claims/${encodeURIComponent(token)}/resend`, {
      method: 'POST',
    });
  }

  /**
   * Update a user's profile and roles within the partner org
   */
  async updatePartnerUser(
    uid: string,
    data: {
      firstName?: string;
      lastName?: string;
      email?: string;
      roles?: string[];
      disabled?: boolean;
    }
  ): Promise<PartnerUserMutationResponse> {
    return this.request(`/api/partner-users/${uid}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  /**
   * Enable or disable a user within the partner org
   */
  async setPartnerUserDisabled(
    uid: string,
    disabled: boolean
  ): Promise<{ uid: string; disabled: boolean; message: string }> {
    return this.request(`/api/partner-users/${uid}/disable`, {
      method: 'PUT',
      body: JSON.stringify({ disabled }),
    });
  }

  /**
   * Remove a user from the partner organization (clears claims, does not delete account)
   */
  async removePartnerUser(uid: string): Promise<{ uid: string; message: string }> {
    return this.request(`/api/partner-users/${uid}`, {
      method: 'DELETE',
    });
  }

  // Transcript download tracking endpoints

  /**
   * Record that transcripts were downloaded (single or bulk PDF)
   */
  async recordTranscriptDownload(
    partnerId: string,
    transcriptIds: number[],
    downloadType: 'single' | 'bulk'
  ): Promise<{ recorded: number }> {
    return this.request(`/api/partners/${partnerId}/transcript-downloads`, {
      method: 'POST',
      body: JSON.stringify({ transcript_ids: transcriptIds, download_type: downloadType }),
    });
  }

  /**
   * Get download status for a set of transcript IDs
   */
  async getTranscriptDownloadStatus(
    partnerId: string,
    transcriptIds: number[]
  ): Promise<TranscriptDownloadStatusResponse> {
    return this.request(`/api/partners/${partnerId}/transcript-downloads/status`, {
      method: 'POST',
      body: JSON.stringify({ transcript_ids: transcriptIds }),
    });
  }

  /**
   * Get portal configuration (e.g. Portal launch date) for a partner
   */
  async getPartnerPortalConfig(partnerId: string): Promise<PartnerPortalConfig> {
    return this.request(`/api/partners/${partnerId}/portal-config`);
  }

  /**
   * Set the partner's Portal launch date (admin only). Pass null to clear.
   */
  async updatePartnerPortalConfig(
    partnerId: string,
    portalLaunchDate: string | null
  ): Promise<PartnerPortalConfig> {
    return this.request(`/api/partners/${partnerId}/portal-config`, {
      method: 'PUT',
      body: JSON.stringify({ portal_launch_date: portalLaunchDate }),
    });
  }

  /**
   * Bulk download multiple transcripts as a zip of PDFs
   */
  async bulkDownloadTranscripts(partnerId: number, ids: number[]): Promise<Blob> {
    const url = `${this.baseUrl}/transcripts/bulk-download`;
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'X-Partner-Id': String(partnerId),
    };
    if (this.idToken) {
      headers['Authorization'] = `Bearer ${this.idToken}`;
    }
    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify({ ids }),
    });
    if (!response.ok) {
      throw new ApiError(`Failed to bulk download: ${response.statusText}`, response.status);
    }
    return response.blob();
  }
}

// ── Token API types (port 8082) ──────────────────────────────────────

export type KeyScope =
  | 'transcripts:read'
  | 'transcripts:download'
  | 'course_mapping:read'
  | 'user_management:read';

export interface ApiKeyItem {
  id: string;
  name: string;
  key_prefix: string;
  scopes: KeyScope[];
  status: 'active' | 'rotating' | 'revoked' | 'expired';
  created_by: string;
  created_at: string;
  expires_at: string;
  expiration_policy: '90d' | '180d' | '1y' | 'custom';
  last_used_at: string | null;
  rotated_from: string | null;
  grace_period_ends_at: string | null;
}

export interface ApiKeyListResponse {
  active: ApiKeyItem[];
  revoked: ApiKeyItem[];
  expired: ApiKeyItem[];
  total: number;
}

export interface CreateApiKeyRequest {
  name: string;
  scopes: KeyScope[];
  expiration_policy: '90d' | '180d' | '1y' | 'custom';
  custom_expires_at?: string;
}

export interface CreateApiKeyResponse {
  id: string;
  name: string;
  key: string;
  key_prefix: string;
  scopes: KeyScope[];
  status: string;
  expires_at: string;
  message: string;
}

export interface RotateKeyResponse {
  new_key: {
    id: string;
    key: string;
    name: string;
    key_prefix: string;
    scopes: KeyScope[];
    status: string;
    expires_at: string;
  };
  old_key: {
    id: string;
    status: string;
    grace_period_ends_at: string;
  };
  message: string;
}

export interface RevokeKeyResponse {
  id: string;
  status: string;
  message: string;
}

export interface RateLimitInfo {
  partner_id: number;
  requests_per_hour: number;
  headers_info: Record<string, string>;
}

/**
 * Create a singleton API client instance
 *
 * Note: You should update the token using setToken() after authentication
 */
export const apiClient = new ApiClient();

/**
 * Token API client for API key management (port 8082)
 */
const TOKEN_API_URL = env.tokenApiUrl;

export function createTokenApi(idToken: string | null): ApiClient {
  return new ApiClient(idToken, TOKEN_API_URL);
}

/** @deprecated Use createTokenApi(idToken) instead */
export const tokenApi = new ApiClient(null, TOKEN_API_URL);

export function getApiKeys(
  partnerId: number,
  client: ApiClient = tokenApi
): Promise<ApiKeyListResponse> {
  return client['request']<ApiKeyListResponse>('/token/api-keys', {
    headers: new Headers({ 'X-Partner-Id': String(partnerId) }),
  });
}

export function createApiKey(
  partnerId: number,
  data: CreateApiKeyRequest,
  client: ApiClient = tokenApi
): Promise<CreateApiKeyResponse> {
  return client['request']<CreateApiKeyResponse>('/token/api-keys', {
    method: 'POST',
    headers: new Headers({ 'X-Partner-Id': String(partnerId) }),
    body: JSON.stringify(data),
  });
}

export function rotateApiKey(
  partnerId: number,
  keyId: string,
  client: ApiClient = tokenApi
): Promise<RotateKeyResponse> {
  return client['request']<RotateKeyResponse>(`/token/api-keys/${keyId}/rotate`, {
    method: 'POST',
    headers: new Headers({ 'X-Partner-Id': String(partnerId) }),
  });
}

export function revokeApiKey(
  partnerId: number,
  keyId: string,
  client: ApiClient = tokenApi
): Promise<RevokeKeyResponse> {
  return client['request']<RevokeKeyResponse>(`/token/api-keys/${keyId}/revoke`, {
    method: 'POST',
    headers: new Headers({ 'X-Partner-Id': String(partnerId) }),
  });
}

export function getRateLimits(
  partnerId: number,
  client: ApiClient = tokenApi
): Promise<RateLimitInfo> {
  return client['request']<RateLimitInfo>('/token/rate-limits', {
    headers: new Headers({ 'X-Partner-Id': String(partnerId) }),
  });
}

// ── Admin User Management types ──────────────────────────────────────

export interface AdminUser {
  uid: string;
  email: string | null;
  displayName: string | null;
  disabled: boolean;
  lastSignInTime: string | null;
  lastRefreshTime: string | null;
  creationTime: string | null;
  customClaims: Record<string, unknown>;
}

export interface AdminUserListResponse {
  users: AdminUser[];
  count: number;
}

export interface UpdateClaimsResponse {
  uid: string;
  email: string | null;
  displayName: string | null;
  customClaims: Record<string, unknown>;
  message: string;
}

// ── Client Partner User Management types ─────────────────────────────

export interface PartnerUserItem {
  uid: string;
  email: string | null;
  firstName: string | null;
  lastName: string | null;
  displayName: string | null;
  disabled: boolean;
  lastSignInTime: string | null;
  roles: string[];
}

export interface PartnerUserListResponse {
  users: PartnerUserItem[];
  total: number;
  page: number;
  per_page: number;
}

export interface PartnerUserMutationResponse {
  uid: string;
  email: string | null;
  firstName: string | null;
  lastName: string | null;
  displayName: string | null;
  disabled: boolean;
  roles: string[];
  message: string;
}

// ── Account-claiming types (SOPHIA-34868) ────────────────────────────

export interface AccountClaimResolveResponse {
  state: 'valid' | 'expired' | 'used' | 'invalid';
  email?: string;
  firstName?: string;
  lastName?: string;
}

export interface AccountClaimResponse {
  email: string;
  /** null when the backend couldn't mint a sign-in token — claim still succeeded */
  customToken: string | null;
  message: string;
}

// ── Partners Admin API types (port 8081) ─────────────────────────────

export type InstitutionType = 'university' | 'college' | 'community_college' | 'technical_school';

export type PartnerRole =
  | 'sophia_admin'
  | 'transcript_viewer'
  | 'course_mapper'
  | 'api_developer'
  | 'academic_advisor'
  | 'bucket_management';

export interface PartnerRecord {
  partner_id: string;
  institution_name: string;
  email_domain: string;
  primary_contact_email: string;
  institution_type: InstitutionType;
  mailing_address?: string;
  phone?: string;
  status: 'active' | 'inactive';
  created_at: string;
  updated_at: string;
  created_by: string;
}

export interface PartnerListResponse {
  items: (PartnerRecord & { user_count: number })[];
  total: number;
  page: number;
  per_page: number;
}

export interface PartnerDetailResponse {
  portal: PartnerRecord | null;
  program: {
    id: number;
    name: string;
    type: string;
    external_partner_name: string | null;
    external_partner_email: string | null;
    send_transcript_to_partner: boolean;
    partnership_tier: string | null;
    partner_account_type: string | null;
    active: boolean;
    logo_url: string | null;
  } | null;
  settings: PartnerSettings | null;
  user_count: number;
}

export interface PortalUser {
  user_id: string;
  email: string;
  display_name: string;
  partner_id: string;
  roles: PartnerRole[];
  last_login?: string;
  created_at: string;
  assigned_by: string;
}

export interface PartnerSettings {
  partner_id: string;
  transcript_bucket_location?: string;
  api_rate_limit: number;
  session_timeout_minutes: number;
  mfa_required: boolean;
  sso_enabled: boolean;
  sso_idp_metadata_url?: string;
  sso_entity_id?: string;
  email_notifications_enabled: boolean;
  default_notification_frequency: 'immediate' | 'daily' | 'weekly' | 'off';
  default_user_roles: PartnerRole[];
  updated_at: string;
  updated_by: string;
}

export interface ActivityLogEntry {
  id: string;
  partner_id: string;
  user_email: string;
  action_type: string;
  details: string;
  ip_address: string;
  user_agent: string;
  timestamp: string;
}

export interface ChangeHistoryEntry {
  id: string;
  partner_id: string;
  entity_type: string;
  entity_id: string;
  field: string;
  old_value: string;
  new_value: string;
  changed_by: string;
  changed_at: string;
}

export interface CreatePartnerRequest {
  institution_name: string;
  email_domain: string;
  primary_contact_email: string;
  institution_type: InstitutionType;
  mailing_address?: string;
  phone?: string;
}

export interface PartnerUsersResponse {
  items: PortalUser[];
  total: number;
}

export interface ActivityLogResponse {
  items: ActivityLogEntry[];
  total: number;
  page: number;
  per_page: number;
}

// ── Partners Admin API client ────────────────────────────────────────

const PARTNERS_API_URL = env.partnersApiUrl;

export function createPartnersApi(idToken: string | null): ApiClient {
  return new ApiClient(idToken, PARTNERS_API_URL);
}

/** @deprecated Use createPartnersApi(idToken) instead */
export const partnersApi = new ApiClient(null, PARTNERS_API_URL);

export function getPartnersList(
  params?: {
    search?: string;
    status?: string;
    page?: number;
    per_page?: number;
  },
  client: ApiClient = partnersApi
): Promise<PartnerListResponse> {
  const qp = new URLSearchParams();
  if (params?.search) qp.set('search', params.search);
  // 'all' must reach the API explicitly — with no status param it defaults to active-only
  if (params?.status) qp.set('status', params.status);
  if (params?.page !== undefined) qp.set('page', String(params.page));
  if (params?.per_page !== undefined) qp.set('per_page', String(params.per_page));
  const q = qp.toString();
  return client['request']<PartnerListResponse>(`/partners${q ? `?${q}` : ''}`);
}

export function getPartnerDetail(
  partnerId: string,
  client: ApiClient = partnersApi
): Promise<PartnerDetailResponse> {
  // X-Partner-Id carries the partner scope for internal (claim-less) users;
  // for partner users it matches their claim and changes nothing.
  return client['request']<PartnerDetailResponse>(`/partners/${partnerId}`, {
    headers: new Headers({ 'X-Partner-Id': String(partnerId) }),
  });
}

export function createPartnerAdmin(
  data: CreatePartnerRequest,
  client: ApiClient = partnersApi
): Promise<PartnerRecord> {
  return client['request']<PartnerRecord>('/partners', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export function updatePartnerAdmin(
  partnerId: string,
  data: Partial<CreatePartnerRequest>,
  client: ApiClient = partnersApi
): Promise<PartnerRecord> {
  return client['request']<PartnerRecord>(`/partners/${partnerId}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export function deactivatePartner(
  partnerId: string,
  client: ApiClient = partnersApi
): Promise<PartnerRecord> {
  return client['request']<PartnerRecord>(`/partners/${partnerId}/deactivate`, {
    method: 'PUT',
  });
}

export function reactivatePartner(
  partnerId: string,
  client: ApiClient = partnersApi
): Promise<PartnerRecord> {
  return client['request']<PartnerRecord>(`/partners/${partnerId}/reactivate`, {
    method: 'PUT',
  });
}

export function getPartnerHistory(
  partnerId: string,
  client: ApiClient = partnersApi
): Promise<{ items: ChangeHistoryEntry[] }> {
  return client['request']<{ items: ChangeHistoryEntry[] }>(`/partners/${partnerId}/history`);
}

export function getPartnerUsers(
  partnerId: string,
  params?: { search?: string; role?: string },
  client: ApiClient = partnersApi
): Promise<PartnerUsersResponse> {
  const qp = new URLSearchParams();
  if (params?.search) qp.set('search', params.search);
  if (params?.role) qp.set('role', params.role);
  const q = qp.toString();
  return client['request']<PartnerUsersResponse>(`/partners/${partnerId}/users${q ? `?${q}` : ''}`);
}

export function addPartnerUser(
  partnerId: string,
  data: { email: string; display_name: string; roles?: PartnerRole[] },
  client: ApiClient = partnersApi
): Promise<PortalUser> {
  return client['request']<PortalUser>(`/partners/${partnerId}/users`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export function removePartnerUser(
  partnerId: string,
  userId: string,
  client: ApiClient = partnersApi
): Promise<{ message: string; user_id: string }> {
  return client['request']<{ message: string; user_id: string }>(
    `/partners/${partnerId}/users/${userId}`,
    { method: 'DELETE' }
  );
}

export function updatePartnerUserRoles(
  partnerId: string,
  userId: string,
  roles: PartnerRole[],
  client: ApiClient = partnersApi
): Promise<PortalUser> {
  return client['request']<PortalUser>(`/partners/${partnerId}/users/${userId}/roles`, {
    method: 'PUT',
    body: JSON.stringify({ roles }),
  });
}

export function getPartnerSettings(
  partnerId: string,
  client: ApiClient = partnersApi
): Promise<PartnerSettings> {
  return client['request']<PartnerSettings>(`/partners/${partnerId}/settings`);
}

export function updatePartnerSettings(
  partnerId: string,
  data: Partial<Omit<PartnerSettings, 'partner_id' | 'updated_at' | 'updated_by'>>,
  client: ApiClient = partnersApi
): Promise<PartnerSettings> {
  return client['request']<PartnerSettings>(`/partners/${partnerId}/settings`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export function getPartnerActivity(
  partnerId: string,
  params?: {
    page?: number;
    per_page?: number;
    date_from?: string;
    date_to?: string;
    user_email?: string;
    action_type?: string;
  },
  client: ApiClient = partnersApi
): Promise<ActivityLogResponse> {
  const qp = new URLSearchParams();
  if (params?.page !== undefined) qp.set('page', String(params.page));
  if (params?.per_page !== undefined) qp.set('per_page', String(params.per_page));
  if (params?.date_from) qp.set('date_from', params.date_from);
  if (params?.date_to) qp.set('date_to', params.date_to);
  if (params?.user_email) qp.set('user_email', params.user_email);
  if (params?.action_type) qp.set('action_type', params.action_type);
  const q = qp.toString();
  return client['request']<ActivityLogResponse>(
    `/partners/${partnerId}/activity${q ? `?${q}` : ''}`
  );
}

export function exportPartnerActivity(
  partnerId: string,
  params?: { date_from?: string; date_to?: string; user_email?: string; action_type?: string }
): Promise<Blob> {
  const qp = new URLSearchParams();
  if (params?.date_from) qp.set('date_from', params.date_from);
  if (params?.date_to) qp.set('date_to', params.date_to);
  if (params?.user_email) qp.set('user_email', params.user_email);
  if (params?.action_type) qp.set('action_type', params.action_type);
  const q = qp.toString();
  const url = `${PARTNERS_API_URL}/partners/${partnerId}/activity/export${q ? `?${q}` : ''}`;
  return fetch(url).then((r) => {
    if (!r.ok) throw new ApiError(`Export failed: ${r.statusText}`, r.status);
    return r.blob();
  });
}

// ── Webhook Config ─────────────────────────────────────────────────

export interface WebhookConfigData {
  partner_id: string;
  enabled: boolean;
  url: string;
  has_signing_secret?: boolean;
  created_at: string;
  updated_at: string;
  updated_by: string;
  last_checked_at: string | null;
  last_delivered_at: string | null;
}

export interface WebhookConfigResponse {
  config: WebhookConfigData | null;
  /** Present only when a signing secret was just generated or rotated; never shown again. */
  signing_secret?: string;
}

export function getWebhookConfig(
  client: ApiClient,
  partnerId?: number | null
): Promise<WebhookConfigResponse> {
  return client['request']<WebhookConfigResponse>('/api/webhooks/config', {
    headers: partnerId != null ? { 'X-Partner-Id': String(partnerId) } : undefined,
  });
}

export function updateWebhookConfig(
  data: { enabled: boolean; url?: string },
  client: ApiClient
): Promise<WebhookConfigResponse> {
  return client['request']<WebhookConfigResponse>('/api/webhooks/config', {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export function rotateWebhookSecret(client: ApiClient): Promise<WebhookConfigResponse> {
  return client['request']<WebhookConfigResponse>('/api/webhooks/config/rotate-secret', {
    method: 'POST',
  });
}
